/**
 * JobCompletionNotifier — global job-completion feedback (UPT-1028 follow-up).
 *
 * Subscribes to STOMP job events (session-launched jobs only, see
 * job_notifications.ts) and slides in a RightDrawer when a job reaches a
 * final state (COMPLETED / FAILED). Mounted once in ClientProviders, so the
 * feedback appears from any page — even after navigating away from the form
 * that launched the job.
 */
import { useEffect, useState } from 'react';
import { useIntl } from '@/react-intl';
import RightDrawer, { DrawerBadge } from '@/uptk/core/components/ui/RightDrawer';
import {
  subscribeJobNotifications,
  type JobNotification,
} from '@/uptk/core/api/job_notifications';

export default function JobCompletionNotifier() {
  const intl = useIntl();
  const [notification, setNotification] = useState<JobNotification | null>(null);

  useEffect(() => {
    return subscribeJobNotifications((event) => {
      if (event.status === 'STARTED') return; // start is confirmed by the launching form
      setNotification(event);
    });
  }, []);

  if (!notification) return null;

  const succeeded = notification.status === 'COMPLETED';

  return (
    <RightDrawer
      isOpen
      onClose={() => setNotification(null)}
      size="lg"
      showBackdrop={false}
      title={intl.formatMessage({ id: 'core.jobs.result.title' })}
      badges={
        <>
          <DrawerBadge variant={succeeded ? 'accent' : 'edit'}>
            {notification.status}
          </DrawerBadge>
          <DrawerBadge>{`JOB: ${notification.jobId}`}</DrawerBadge>
        </>
      }
      headerActions={
        notification.exitDescription ? (
          <button
            type="button"
            className="ui-button ui-button-secondary inline-flex items-center gap-2 whitespace-nowrap px-3 py-1.5 text-xs leading-none"
            onClick={() => { void navigator.clipboard?.writeText(notification.exitDescription ?? ''); }}
          >
            {intl.formatMessage({ id: 'core.fileloading.result.copyDetails' })}
          </button>
        ) : undefined
      }
    >
      <div className="space-y-3 text-sm text-portal-ink">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-portal-ink-muted">
              {intl.formatMessage({ id: 'core.jobs.result.name' })}
            </div>
            <div>{notification.jobName}</div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-portal-ink-muted">
              {intl.formatMessage({ id: 'core.jobs.result.completedAt' })}
            </div>
            <div>
              {notification.completedAt
                ? new Date(notification.completedAt).toLocaleString()
                // eslint-disable-next-line i18next/no-literal-string -- i18n-exempt: neutral placeholder glyph
                : '—'}
            </div>
          </div>
        </div>
        {notification.exitDescription && (
          <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-lg bg-portal-surface-muted p-3 text-xs">
            {notification.exitDescription}
          </pre>
        )}
      </div>
    </RightDrawer>
  );
}
